import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Shuffle, RefreshCw, PackageOpen } from 'lucide-react';
import { DashboardLayout, PageHeader } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import ListPagination from '../common/ListPagination.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import SelectField from '../common/SelectField.js';
import CrossingVehicleSelect from './CrossingVehicleSelect.js';
import shipmentCrossingService from '../../services/shipmentCrossingService.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import { paginateArray } from '../../utils/clientPaginate.js';

const PER_PAGE = 10;

const ShipmentCrossingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectId = searchParams.get('shipment_id');
  const stepParam = searchParams.get('step');
  const initialStep = stepParam === 'dispatch' ? 'dispatch' : stepParam === 'missing' ? 'missing' : 'unload';

  const [step, setStep] = useState(initialStep);
  const [shipments, setShipments] = useState([]);
  const [mlSheets, setMlSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mlSheetId, setMlSheetId] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedIds, setSelectedIds] = useState(() => (preselectId ? [preselectId] : []));
  const [vehicle, setVehicle] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const perPage = PER_PAGE;
  const isUnload = step === 'unload';
  const isMissing = step === 'missing';

  const loadMlSheets = useCallback(async () => {
    if (step === 'missing') return;
    try {
      const res = await shipmentCrossingService.listCrossableMlSheets({ action: step });
      setMlSheets(res?.items || []);
    } catch {
      setMlSheets([]);
    }
  }, [step]);

  const loadShipments = useCallback(async () => {
    try {
      setLoading(true);
      if (step === 'missing') {
        const res = await shipmentCrossingService.listShortfalls({
          search: search || undefined,
          mlSheetId: mlSheetId || undefined,
        });
        setShipments(res?.items || []);
        setMlSheets(res?.ml_sheets || []);
        setTotalPages(1);
        return;
      }
      const res = await shipmentCrossingService.listCrossableShipments({
        page,
        perPage,
        search: search || undefined,
        mlSheetId: mlSheetId || undefined,
        action: step,
      });
      setShipments((prev) => mergeLoadedPage(page, res?.items || [], prev));
      setTotalPages(res?.pages || 1);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load shipments');
      if (page <= 1) setShipments([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, mlSheetId, perPage, step]);

  useEffect(() => {
    loadMlSheets();
  }, [loadMlSheets]);

  usePaginatedListFetch({
    fetchPage: loadShipments,
    setCurrentPage: setPage,
    currentPage: page,
    resetPageDeps: [search, mlSheetId, step],
    debounceWhen: search,
    debounceMs: 0,
  });

  const mlSheetOptions = useMemo(
    () => [
      { value: '', label: 'All ML sheets' },
      ...mlSheets.map((sheet) => ({
        value: sheet.ml_sheet_id,
        label: `${sheet.ml_sheet_id}${sheet.vehicle_number ? ` · ${sheet.vehicle_number}` : ''} (${sheet.shipment_count})`,
      })),
    ],
    [mlSheets],
  );

  const changeStep = (nextStep) => {
    if (nextStep === step) return;
    setStep(nextStep);
    setPage(1);
    setSelectedIds([]);
    setVehicle(null);
    setRemarks('');
    setMlSheetId('');
    const next = new URLSearchParams(searchParams);
    next.set('step', nextStep);
    setSearchParams(next, { replace: true });
  };

  const toggleSelect = (shipmentId) => {
    setSelectedIds((prev) =>
      prev.includes(shipmentId)
        ? prev.filter((id) => id !== shipmentId)
        : [...prev, shipmentId],
    );
  };

  const memoryView = isMissing ? paginateArray(shipments, page, perPage) : null;
  const visibleShipments = memoryView ? memoryView.items : shipments;
  const listTotalPages = memoryView ? memoryView.totalPages : totalPages;

  const eligibleOnPage = visibleShipments.filter((s) => s.eligible);
  const allSelectedOnPage =
    eligibleOnPage.length > 0 && eligibleOnPage.every((s) => selectedIds.includes(s.shipment_id));

  const toggleSelectAll = () => {
    const pageIds = eligibleOnPage.map((s) => s.shipment_id);
    if (allSelectedOnPage) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleMlSheetChange = (event) => {
    setMlSheetId(event.target.value);
    setPage(1);
    setSelectedIds([]);
  };

  const handleRefresh = () => {
    loadMlSheets();
    loadShipments();
  };

  const handleUnload = async () => {
    if (selectedIds.length === 0) {
      Alert.error('Select at least one shipment to unload');
      return;
    }

    try {
      setSubmitting(true);
      const response = await shipmentCrossingService.unloadShipments({
        shipmentIds: selectedIds,
        remarks: remarks.trim() || undefined,
      });

      if (response.failed_count > 0) {
        Alert.error(response.message || 'Some unloads failed');
      } else {
        Alert.success(response.message || 'Shipments unloaded at crossing hub');
      }

      setSelectedIds([]);
      setRemarks('');
      loadMlSheets();
      loadShipments();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to unload shipments');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateCrossing = async () => {
    if (selectedIds.length === 0) {
      Alert.error('Select at least one shipment');
      return;
    }
    if (!vehicle) {
      Alert.error('Select a vehicle for dispatch');
      return;
    }

    try {
      setSubmitting(true);
      const response = await shipmentCrossingService.createCrossing({
        shipmentIds: selectedIds,
        newVehicleId: vehicle.id,
        remarks: remarks.trim() || undefined,
      });

      if (response.failed_count > 0) {
        Alert.error(response.message || 'Some dispatches failed');
      } else {
        Alert.success(response.message || 'Dispatched after crossing successfully');
      }

      setSelectedIds([]);
      setVehicle(null);
      setRemarks('');
      loadMlSheets();
      loadShipments();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to dispatch after crossing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkMissing = async () => {
    if (selectedIds.length === 0) {
      Alert.error('Select the shipments that are not on the vehicle');
      return;
    }
    if (!remarks.trim()) {
      Alert.error('Add a note for the missing parcels');
      return;
    }

    try {
      setSubmitting(true);
      const response = await shipmentCrossingService.markMissing({
        shipmentIds: selectedIds,
        note: remarks.trim(),
        source: 'crossing',
      });
      if (response.failed_count > 0) {
        Alert.error(response.message || 'Some parcels were not marked missing');
      } else {
        Alert.success(response.message || 'Marked missing');
      }
      setSelectedIds([]);
      setRemarks('');
      loadMlSheets();
      loadShipments();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to mark missing');
    } finally {
      setSubmitting(false);
    }
  };

  const emptyMessage = isMissing
    ? 'No parcels marked missing at this crossing hub.'
    : isUnload
    ? mlSheetId
      ? 'No Dispatched shipments to unload on this ML sheet.'
      : 'No Dispatched shipments at your crossing location. Unload must happen before dispatch again.'
    : mlSheetId
      ? 'No unloaded (Received) shipments on this ML sheet ready to dispatch.'
      : 'No unloaded shipments ready to dispatch. Unload first, then select a new vehicle.';

  return (
    <DashboardLayout>
      <PageHeader
        title="Shipment Crossing"
        subtitle="Unload what arrived, mark what did not, then dispatch again. The ML sheet, on contract, and commission stay unchanged."
      >
        <Button variant="secondary" icon={RefreshCw} onClick={handleRefresh}>
          Refresh
        </Button>
      </PageHeader>

      <div className="mb-4 grid grid-cols-3 gap-1 p-1 bg-gray-100 rounded-lg max-w-xl">
        <button
          type="button"
          onClick={() => changeStep('unload')}
          className={`py-2 text-sm font-medium rounded-md transition-colors ${
            isUnload ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
          }`}
        >
          1. Unload
        </button>
        <button
          type="button"
          onClick={() => changeStep('dispatch')}
          className={`py-2 text-sm font-medium rounded-md transition-colors ${
            !isUnload ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
          }`}
        >
          2. Dispatch again
        </button>
        <button
          type="button"
          onClick={() => changeStep('missing')}
          className={`py-2 text-sm font-medium rounded-md transition-colors ${
            isMissing ? 'bg-white text-red-600 shadow-sm' : 'text-gray-600'
          }`}
        >
          Missing
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
          <div className="mb-3">
            <SearchSubmitInput
              value={search}
              onSearch={setSearch}
              placeholder="Search by shipment ID, ML sheet, destination, or vehicle..."
              inputClassName="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </div>

          <div className="mb-3">
            <SelectField
              variant="filter"
              label="Filter by ML sheet"
              value={mlSheetId}
              onChange={handleMlSheetChange}
              options={mlSheetOptions}
            />
          </div>

          {loading && page === 1 ? (
            <div className="py-10">
              <Loader fullScreen={false} size="small" message="Loading shipments..." />
            </div>
          ) : visibleShipments.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">{emptyMessage}</div>
          ) : (
            <>
              <div className="flex items-center gap-2 px-1 pb-2 border-b border-gray-100">
                <input
                  type="checkbox"
                  checked={allSelectedOnPage}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 accent-blue-500"
                  id="select-all-crossing"
                />
                <label htmlFor="select-all-crossing" className="text-xs font-medium text-gray-600">
                  {mlSheetId ? 'Select all eligible on this ML sheet' : 'Select all eligible on this page'}
                </label>
              </div>

              <div className="divide-y divide-gray-100 max-h-[52vh] overflow-y-auto">
                {visibleShipments.map((shipment) => {
                  const selected = selectedIds.includes(shipment.shipment_id);
                  return (
                    <label
                      key={shipment.shipment_id}
                      className={`flex items-start gap-3 p-3 text-sm cursor-pointer ${
                        selected ? 'bg-orange-50/70' : 'hover:bg-gray-50'
                      } ${!shipment.eligible ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={!shipment.eligible}
                        onChange={() => toggleSelect(shipment.shipment_id)}
                        className="mt-0.5 w-4 h-4 accent-blue-500"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <strong className="text-gray-800">{shipment.shipment_id}</strong>
                          <span className="capitalize text-gray-600">{shipment.status}</span>
                        </div>
                        <p className="text-gray-600 mt-0.5">
                          {shipment.sender_location} → {shipment.receiver_location}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Current vehicle: {shipment.current_vehicle_number || 'N/A'}
                          {shipment.ml_sheet_id ? ` · ML: ${shipment.ml_sheet_id}` : ''}
                        </p>
                        {!shipment.eligible && shipment.reason && (
                          <p className="text-xs text-amber-700 mt-0.5">{shipment.reason}</p>
                        )}
                        {shipment.shortfall?.note && (
                          <p className="text-xs text-red-700 mt-0.5">
                            {shipment.shortfall.note}
                            {shipment.shortfall.marked_by_name ? ` · ${shipment.shortfall.marked_by_name}` : ''}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
                <ListPagination
                  currentPage={page}
                  totalPages={listTotalPages}
                  loading={loading}
                  onNext={() => setPage((nextPage) => nextPage + 1)}
                />
              </div>
              {mlSheetId && (
                <p className="text-xs text-gray-500 mt-3">
                  Showing {shipments.length} shipment{shipments.length === 1 ? '' : 's'} on {mlSheetId}
                </p>
              )}
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 h-fit lg:sticky lg:top-4 space-y-4">
          <div className="flex items-center gap-2">
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                isMissing ? 'bg-red-50 text-red-600' : isUnload ? 'bg-teal-50 text-teal-600' : 'bg-blue-50 text-blue-600'
              }`}
            >
              {isUnload || isMissing ? <PackageOpen className="w-4 h-4" /> : <Shuffle className="w-4 h-4" />}
            </div>
            <h3 className="text-base font-bold text-gray-800">
              {isMissing ? 'Missing at hub' : isUnload ? 'Unload at hub' : 'Dispatch again'}
            </h3>
          </div>

          <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 space-y-1">
            <div>
              <span className="font-semibold text-gray-800">{selectedIds.length}</span> shipment(s) selected
            </div>
            {mlSheetId ? (
              <div className="text-xs text-gray-500">ML sheet: {mlSheetId}</div>
            ) : null}
          </div>

          {!isUnload && !isMissing && (
            <CrossingVehicleSelect value={vehicle} onChange={setVehicle} disabled={submitting} />
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isUnload ? 'Note' : 'Remarks (optional)'}
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              placeholder={
                isMissing
                  ? 'Add a note for this unload...'
                  : isUnload
                  ? 'Required when marking missing. Optional for unload.'
                  : 'Add a note for this dispatch...'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </div>

          {isUnload || isMissing ? (
            <div className="space-y-2">
              <Button
                variant="primary"
                icon={PackageOpen}
                fullWidth
                loading={submitting}
                disabled={submitting || selectedIds.length === 0}
                onClick={handleUnload}
              >
                Unload{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
              </Button>
              {isUnload && (
                <Button
                  variant="secondary"
                  fullWidth
                  loading={submitting}
                  disabled={submitting || selectedIds.length === 0}
                  onClick={handleMarkMissing}
                >
                  Mark missing{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
                </Button>
              )}
            </div>
          ) : (
            <Button
              variant="primary"
              icon={Shuffle}
              fullWidth
              loading={submitting}
              disabled={submitting || selectedIds.length === 0 || !vehicle}
              onClick={handleCreateCrossing}
            >
              Dispatch again{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
            </Button>
          )}

          <p className="text-xs text-gray-400">
            {isMissing
              ? 'The parcel stays on Unload as well. Unload it when it arrives. That closes the missing record. The destination cannot receive it until this hub unloads it.'
              : isUnload
              ? 'Unload marks the shipment Received. Mark missing leaves it on this list as Dispatched and creates a missing record. The bell goes to company admin and the previous location. Unload it here when the parcel arrives to close the missing.'
              : 'Dispatch again changes the carrying vehicle and records the crossing. It does not change the ML sheet, on contract, or commission.'}
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ShipmentCrossingPage;
